/**
 * Service Providers repository — the data-access home for the `service_providers` slice.
 *
 * Strangler-Fig per-slice migration (manifest-6 / Track A10):
 *   Step 1 (THIS) — consolidated scattered `supabase.from("service_providers")`
 *                    and `supabase.from("service_providers_with_status")` here.
 *   Step 2 (done) — guarded NestJS endpoints (`apps/api/src/service-providers/*`).
 *   Step 3 (HERE) — STAFF service-provider ops flipped to the Nest client;
 *                    CROSS-DOMAIN reads and PROVIDER-PORTAL ops stay on Supabase/RLS.
 *
 * ── Flip scope (owner-vs-staff authz split) ──────────────────────────────────
 *   FLIPPED to Nest (with a Supabase fallback): `listWithStatus`, `getById`,
 *     `create`, `update`, `softDelete` — all reached only by the STAFF dashboard
 *     (Maintenance.tsx, MaintenanceRequestDetails.tsx), whose callers hold
 *     `maintenance.{view,create,edit,delete}`.
 *   KEPT on Supabase/RLS (documented — shared cross-domain reads):
 *     - `listForReports` — used by useReportsData (read-only join in reports)
 *     - `count` — used by useDashboardStats (aggregate count)
 *     - provider-portal reads in useServiceProviderData.ts
 *
 * ── Convention audit (delete semantics) ──────────────────────────────────────
 *   `service_providers` has `deleted_at` + `is_active` columns. The existing
 *   `useDeleteServiceProvider` soft-deletes (`deleted_at`, `is_active = false`).
 *   This matches the pattern and is preserved. No hard delete.
 */

import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { serviceProviderSchema } from "@dallali/shared";
import { serializeError } from "@/lib/sentry";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowAuth,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";

const TABLE = "service_providers";
const VIEW_TABLE = "service_providers_with_status";

const nestRequest = makeNestRequest("serviceProviders");

type ServiceProviderRow = import("@/integrations/supabase/types").Database["public"]["Tables"]["service_providers"]["Row"];
type ServiceProviderInsert = import("@/integrations/supabase/types").Database["public"]["Tables"]["service_providers"]["Insert"];
// user_id exists in the DB (migration 20260621250000_service_providers_user_id)
// but the generated Supabase types predate it — extend until types are regenerated.
type ServiceProviderUpdate =
  import("@/integrations/supabase/types").Database["public"]["Tables"]["service_providers"]["Update"] & {
    user_id?: string | null;
  };

// ── Supabase implementations ──────────────────────────────────────────────

async function listWithStatusViaSupabase(companyId: string) {
  // View not in generated types, so we use `as any` for the view name
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from(VIEW_TABLE)
    .select("*")
    .eq("company_id", companyId)
    .is("deleted_at", null)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching service providers:", serializeError(error));
    throw error;
  }
  const rows = (data ?? []) as (ServiceProviderRow & { status?: string })[];
  return rows.map((sp) => ({
    ...sp,
    invitation_status: sp.status || "not_invited",
  })) as ServiceProviderRow[];
}

type ListWithStatusResult = Awaited<ReturnType<typeof listWithStatusViaSupabase>>;

async function getByIdViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .eq("id", id)
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching service provider:", serializeError(error));
    throw error;
  }
  return data as ServiceProviderRow | null;
}

async function createViaSupabase(companyId: string, data: Omit<ServiceProviderInsert, "id" | "company_id" | "created_at" | "updated_at">) {
  const { data: result, error } = await supabase
    .from(TABLE)
    .insert({ ...data, company_id: companyId })
    .select()
    .maybeSingle();

  if (error) {
    console.error("Error creating service provider:", serializeError(error));
    throw error;
  }
  if (!result) {
    throw new Error("Service provider create returned no data");
  }
  return result;
}

async function updateViaSupabase(id: string, companyId: string, data: ServiceProviderUpdate) {
  const { data: result, error } = await supabase
    .from(TABLE)
    .update(data)
    .eq("id", id)
    .eq("company_id", companyId)
    .select()
    .maybeSingle();

  if (error) {
    console.error("Error updating service provider:", serializeError(error));
    throw error;
  }
  if (!result) throw new Error("Service provider not found or access denied");
  return result as ServiceProviderRow;
}

async function softDeleteViaSupabase(id: string, companyId: string): Promise<void> {
  // Soft-delete the provider
  const { error } = await supabase
    .from(TABLE)
    .update({ deleted_at: new Date().toISOString(), is_active: false })
    .eq("id", id)
    .eq("company_id", companyId);

  if (error) {
    console.error("Error deleting service provider:", serializeError(error));
    throw error;
  }

  // Cancel any pending invitation for this service provider
  const { data: sp } = await supabase
    .from(TABLE)
    .select("phone")
    .eq("id", id)
    .eq("company_id", companyId)
    .maybeSingle();

  if (sp?.phone) {
    const placeholderEmail = `${sp.phone}@placeholder.dallali.sa`;
    await supabase
      .from("invitations")
      .update({ status: "cancelled" })
      .eq("company_id", companyId)
      .eq("email", placeholderEmail)
      .eq("status", "pending");
  }
}

async function listForReportsViaSupabase(companyId: string) {
  const { data, error } = await supabase
    .from(TABLE)
    .select("id, name, name_en, total_jobs, rating")
    .eq("company_id", companyId)
    .eq("is_active", true);

  if (error) {
    console.error("Error fetching service providers for reports:", serializeError(error));
    throw error;
  }
  return data ?? [];
}

async function countViaSupabase(companyId: string) {
  const { count, error } = await supabase
    .from(TABLE)
    .select("id", { count: "exact", head: true })
    .eq("company_id", companyId)
    // Convention fix: exclude soft-deleted providers (deleted_at) — the dashboard
    // count widget previously counted dead rows. All other reads filter deleted_at.
    .is("deleted_at", null);

  if (error) {
    console.error("Error counting service providers:", serializeError(error));
    throw error;
  }
  return count ?? 0;
}

export const serviceProvidersRepository = {
  // ─── STAFF ops (flipped to Nest, Supabase fallback) ───────────────────

  /**
   * List all service providers with invitation status from the view.
   * Staff op — flipped to Nest, Supabase fallback.
   */
  async listWithStatus(companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.serviceProviders.list());
        return z.array(serviceProviderSchema).parse(rows) as unknown as ListWithStatusResult;
      } catch (err) {
        // service_providers_with_status is security_invoker=true — re-throw auth
        // errors so the Supabase fallback isn't called with no session (42501).
        reportNestFallbackOrRethrowAuth("serviceProviders.listWithStatus", err);
      }
    }
    return listWithStatusViaSupabase(companyId);
  },

  /**
   * Get a single service provider by id. Company-scoped.
   * Staff op — flipped to Nest, Supabase fallback.
   */
  async getById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.serviceProviders.getById(id));
        return (row ? serviceProviderSchema.parse(row) : null) as unknown as ServiceProviderRow | null;
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("serviceProviders.getById", err);
      }
    }
    return getByIdViaSupabase(id, companyId);
  },

  /**
   * Create a new service provider. company_id injected from the call-site.
   * Staff op — flipped to Nest (POST), Supabase fallback on a safe 4xx.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the Supabase
   * fallback only runs when Nest verifiably did not commit (safe 4xx /
   * unconfigured). An ambiguous outcome (network, timeout, 5xx, parse failure
   * after a 2xx) RETHROWS instead of re-issuing the insert — a fallback after a
   * committed-but-lost Nest write would double-create the service provider.
   */
  async create(companyId: string, data: Omit<ServiceProviderInsert, "id" | "company_id" | "created_at" | "updated_at">): Promise<ServiceProviderRow> {
    if (API_URL) {
      try {
        const result = await nestRequest<ServiceProviderRow>(API_ROUTES.serviceProviders.create(), {
          method: "POST",
          body: JSON.stringify({ ...data, company_id: companyId }),
        });
        return result;
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("serviceProviders.create", err);
      }
    }
    return createViaSupabase(companyId, data);
  },

  /**
   * Update mutable fields of a service provider. Company-scoped.
   * Staff op — flipped to Nest (PUT), Supabase fallback.
   */
  async update(id: string, companyId: string, data: ServiceProviderUpdate) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.serviceProviders.update(id), {
          method: "PUT",
          body: JSON.stringify(data),
        });
        return serviceProviderSchema.parse(row);
      } catch (err) {
        reportNestFallback("serviceProviders.update", err);
      }
    }
    return updateViaSupabase(id, companyId, data);
  },

  /**
   * Soft-delete a service provider (set deleted_at + is_active=false).
   * Also cancels pending invitations. Company-scoped.
   * Staff op — flipped to Nest (DELETE → 204), Supabase fallback.
   */
  async softDelete(id: string, companyId: string): Promise<void> {
    if (API_URL) {
      try {
        await nestRequest<void>(API_ROUTES.serviceProviders.remove(id), { method: "DELETE" });
        return;
      } catch (err) {
        reportNestFallback("serviceProviders.softDelete", err);
      }
    }
    return softDeleteViaSupabase(id, companyId);
  },

  // ─── PROVIDER self-service (portal) — Nest-only, server resolves identity ─────
  // No Supabase fallback: the by-phone provider lookup these replace has been
  // removed (identity is resolved server-side via the user_id FK). The endpoints
  // are fail-closed — a non-linked caller gets 404.

  /** The authenticated provider's own profile row. */
  async getMe(): Promise<ServiceProviderRow> {
    // nestRequest throws NestRequestError on 404 (non-linked caller) — it never
    // returns falsy, so the caller surfaces "not a provider" via the query error.
    const row = await nestRequest<unknown>(API_ROUTES.serviceProviders.me());
    return serviceProviderSchema.parse(row) as unknown as ServiceProviderRow;
  },

  /** Update the authenticated provider's own profile (whitelisted fields). */
  async updateMe(data: {
    name?: string;
    name_en?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    specialty?: string;
    notes?: string | null;
  }) {
    const row = await nestRequest<unknown>(API_ROUTES.serviceProviders.me(), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    return serviceProviderSchema.parse(row);
  },

  // ─── CROSS-DOMAIN reads (KEPT on Supabase/RLS — no flip) ─────────────

  /**
   * Lightweight provider list for reports (join data). Supabase only.
   */
  async listForReports(companyId: string) {
    return listForReportsViaSupabase(companyId);
  },

  /**
   * Count of service providers in a company. Supabase only.
   */
  async count(companyId: string) {
    return countViaSupabase(companyId);
  },
};
